import { getStateFromJwt } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/get-state-from-jwt';
import { JWTRoles } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/interfaces/jwt-user-payload.interface';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { AuthenticationStateModel } from '@app/core/authentication/authentication-state.model';
import { InAppStorage } from '@app/core/storage/in-app-storage';
import type { RootState } from '@app/core/store';
import { Permission } from '@model/permission.model';

export const defaultState: AuthenticationStateModel = {
  mustResetPassword: null,
  token: null,
  userId: null,
  customerId: null,
  roles: null,
  networks: null,
  permissions: null,
  permissionsLoaded: false,
};

const { jwt } = InAppStorage;
const initialState: AuthenticationStateModel = getStateFromJwt(
  jwt,
  defaultState,
);

export const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    authenticate: (
      currentState: AuthenticationStateModel,
      action: PayloadAction<string>,
    ): AuthenticationStateModel => {
      return getStateFromJwt(action.payload, currentState);
    },
    disconnect: (): AuthenticationStateModel => {
      return defaultState;
    },
    setPermissions: (
      currentState: AuthenticationStateModel,
      action: PayloadAction<Permission[]>,
    ): AuthenticationStateModel => {
      return {
        ...currentState,
        permissions: action.payload,
        permissionsLoaded: true,
      };
    },
  },
});

export const { authenticate, disconnect, setPermissions } =
  authenticationSlice.actions;

export const isAuthenticated = (state: RootState): boolean =>
  !!state.authentication.userId;

export const userId = (state: RootState): string | null =>
  state.authentication.userId;

export const getJwt = (state: RootState): string | null =>
  state.authentication.token;

export const roles = (state: RootState): JWTRoles[] | null =>
  state.authentication.roles;

export const permissions = (
  state: RootState,
): Permission[] | null | undefined => state.authentication.permissions;

export const permissionsLoaded = (state: RootState): boolean =>
  state.authentication.permissionsLoaded;

export const mustResetPassword = (state: RootState): boolean | null =>
  state.authentication.mustResetPassword;

export const authenticationReducer = authenticationSlice.reducer;
